import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { useDispatch, useSelector } from '_common/hooks';
import {
  EditElementStatusProps,
  selectOrderedElementStatusList,
  useEditElementStatusMutation,
  useGetElementStatusListQuery,
} from '_common/services/api/elementStatusApi';
import { openAndUpdateModal } from '_common/modals/ModalsSlice';

import { Status } from '_common/components';
import { Table, Cell, type TableColumn, type TableRow } from '_common/components/Table';

import styles from './ElementStatusTable.module.scss';

const ElementStatusTable = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const { data } = useGetElementStatusListQuery();
  const list = useSelector(selectOrderedElementStatusList);

  const [editElementStatus] = useEditElementStatusMutation();

  const canMoveDown = (index: number) => index !== 0 && index !== list.length - 1;
  const canMoveUp = (index: number) => index > 1;

  const move = (id: string, position: number) => {
    const status = list.find((item) => item.id === id);
    if (status) {
      const payload: EditElementStatusProps['status'] = {
        name: status.name,
        color: status.color,
        visible: status.visible,
        allow_move: status.allow_move,
        allow_delete: status.allow_delete,
        allow_edit: status.allow_edit,
        confirm_input: status.confirm_input,
        position,
      };
      editElementStatus({ id, status: payload });
    }
  };

  const openEditModal = (id: string) => {
    dispatch(openAndUpdateModal({ modal: 'ElementStatusFormModal', data: { mode: 'edit', id } }));
  };

  const openDeleteModal = (id: string) => {
    dispatch(openAndUpdateModal({ modal: 'DeleteElementStatusModal', data: { id } }));
  };

  const columns = useMemo<TableColumn<ElementStatus>[]>(() => {
    return [
      {
        id: 'id',
        accessorKey: 'name',
        size: 200,
        header: intl.formatMessage({ id: 'STATUS' }),
        cell: (info) => {
          const status = info.row.original;
          return (
            <Cell testId={`${status.name.toLowerCase()}-status-column`}>
              <Status size="s" id={status.id} testId="status" fullWidth />
            </Cell>
          );
        },
      },
      {
        id: 'allow_edit',
        accessorKey: 'allow_edit',
        enableSorting: false,
        size: 120,
        header: intl.formatMessage({ id: 'global.edit' }),
        cell: (info) => {
          const status = info.row.original;
          return (
            <Cell testId={`${status.name.toLowerCase()}-edit-column`}>
              <Cell.Boolean
                value={status.allow_edit}
                testId={`${status.name.toLowerCase()}-edit`}
              />
            </Cell>
          );
        },
      },
      {
        id: 'allow_move',
        accessorKey: 'allow_move',
        enableSorting: false,
        size: 120,
        header: intl.formatMessage({ id: 'global.move' }),
        cell: (info) => {
          const status = info.row.original;
          return (
            <Cell testId={`${status.name.toLowerCase()}-move-column`}>
              <Cell.Boolean
                value={status.allow_move}
                testId={`${status.name.toLowerCase()}-move`}
              />
            </Cell>
          );
        },
      },
      {
        id: 'allow_delete',
        accessorKey: 'allow_delete',
        enableSorting: false,
        size: 120,
        header: intl.formatMessage({ id: 'global.delete' }),
        cell: (info) => {
          const status = info.row.original;
          return (
            <Cell testId={`${status.name.toLowerCase()}-delete-column`}>
              <Cell.Boolean
                value={status.allow_delete}
                testId={`${status.name.toLowerCase()}-delete`}
              />
            </Cell>
          );
        },
      },
      {
        id: 'visible',
        accessorKey: 'visible',
        enableSorting: false,
        size: 120,
        header: intl.formatMessage({ id: 'VISIBLE_STATUS' }),
        cell: (info) => {
          const status = info.row.original;
          return (
            <Cell testId={`${status.name.toLowerCase()}-visible-column`}>
              <Cell.Boolean
                value={status.visible}
                testId={`${status.name.toLowerCase()}-visible`}
              />
            </Cell>
          );
        },
      },
      {
        id: 'confirm_input',
        accessorKey: 'confirm_input',
        enableSorting: false,
        size: 120,
        header: intl.formatMessage({ id: 'CONFIRMATION' }),
        cell: (info) => {
          const status = info.row.original;
          return (
            <Cell testId={`${status.name.toLowerCase()}-confirmation-request-column`}>
              <Cell.Boolean
                value={status.confirm_input}
                testId={`${status.name.toLowerCase()}-confirmation-request`}
              />
            </Cell>
          );
        },
      },
    ];
  }, []);

  const rowsData = useMemo(() => {
    return list.map<TableRow<ElementStatus>>((status, index) => {
      return {
        ...status,
        suffix: {
          actions: [
            {
              label: intl.formatMessage({ id: 'MOVE_STATUS_UP' }),
              disabled: !canMoveUp(index),
              onClick: () => move(status.id, Number(status.position) - 1),
            },
            {
              label: intl.formatMessage({ id: 'MOVE_STATUS_DOWN' }),
              disabled: !canMoveDown(index),
              onClick: () => move(status.id, Number(status.position) + 1),
            },
            {
              label: intl.formatMessage({ id: 'EDIT_STATUS' }),
              onClick: () => openEditModal(status.id),
            },
            ...(status.id !== 'active' && status.id !== 'approved'
              ? [
                  {
                    label: intl.formatMessage({ id: 'DELETE_STATUS' }),
                    onClick: () => openDeleteModal(status.id),
                  },
                ]
              : []),
          ],
        },
      };
    });
  }, [list, data]);

  return (
    <div className={styles.content}>
      <div className={styles.tableWrapper}>
        <Table
          identity="status"
          selectable={false}
          options={{ columns, data: rowsData }}
          renderFooter={() => null}
          isLoading={false}
          loadingLabel={intl.formatMessage({ id: 'LOADING_ELEMENTS' })}
          testId="status"
        />
      </div>
    </div>
  );
};

export default ElementStatusTable;
